import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createUser,
  updateUser,
  requestPhoneUpdate,
  confirmPhoneUpdate,
  requestEmailUpdate,
  confirmEmailUpdate,
  requestSettlementAccountUpdate,
  confirmSettlementAccountUpdate,
} from './request';
import type { UserUpdatePayload } from './payload';
import { USERS_QUERY_KEY, USER_QUERY_KEY } from '@/api/queries/user/key';
import { BUSINESS_QUERY_KEY } from '@/api/queries/business/key';
import toast from 'react-hot-toast';
import type { UserData } from './response';

export const useCreateUser = (onSuccess?: (data: UserData) => void) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createUser,
    onSuccess: (response) => {
      toast.success(response.response);
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] });
      onSuccess?.(response.data);
    },
  });
};

export const useUpdateUser = (id: string, onSuccess?: () => void) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UserUpdatePayload) => updateUser(id, payload),
    onSuccess: ({ response }) => {
      toast.success(response);
      queryClient.invalidateQueries({ queryKey: [USER_QUERY_KEY, id] });
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] });
      onSuccess?.();
    },
  });
};

export const useRequestPhoneUpdate = (onSuccess?: () => void) => {
  return useMutation({
    mutationFn: requestPhoneUpdate,
    onSuccess: (response) => {
      toast.success(response.response);
      onSuccess?.();
    },
  });
};

export const useConfirmPhoneUpdate = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: confirmPhoneUpdate,
    onSuccess: (response) => {
      toast.success(response.response);
      queryClient.invalidateQueries({ queryKey: [USER_QUERY_KEY, 'me'] });
      onSuccess?.();
    },
  });
};

export const useRequestEmailUpdate = (onSuccess?: () => void) => {
  return useMutation({
    mutationFn: requestEmailUpdate,
    onSuccess: (response) => {
      toast.success(response.response);
      onSuccess?.();
    },
  });
};

export const useConfirmEmailUpdate = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: confirmEmailUpdate,
    onSuccess: (response) => {
      toast.success(response.response);
      queryClient.invalidateQueries({ queryKey: [USER_QUERY_KEY, 'me'] });
      onSuccess?.();
    },
  });
};

export const useRequestSettlementAccountUpdate = (
  onSuccess?: (
    data: Awaited<ReturnType<typeof requestSettlementAccountUpdate>>
  ) => void
) => {
  return useMutation({
    mutationFn: requestSettlementAccountUpdate,
    onSuccess: (response) => {
      toast.success(response.response || 'OTP sent to your email address');
      onSuccess?.(response);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Could not send verification code');
    },
  });
};

export const useConfirmSettlementAccountUpdate = (
  onSuccess?: (
    data: Awaited<ReturnType<typeof confirmSettlementAccountUpdate>>
  ) => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: confirmSettlementAccountUpdate,
    onSuccess: (response) => {
      toast.success(response.response || 'Bank account updated');
      void queryClient.invalidateQueries({ queryKey: [USER_QUERY_KEY, 'me'] });
      void queryClient.invalidateQueries({ queryKey: [BUSINESS_QUERY_KEY] });
      onSuccess?.(response);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Could not confirm bank account update');
    },
  });
};
